/**
 * PowerSync IPC bridge types — Renderer Process
 *
 * Mirrors the type signatures exposed by preload.ts via contextBridge.
 * Import these wherever the renderer needs to type PowerSync IPC results.
 */

export interface PowerSyncExecuteResult {
  success: boolean;
  rowsAffected?: number;
  error?: string;
}

export interface PowerSyncQueryResult<T = Record<string, unknown>> {
  success: boolean;
  data: T | null;
  error?: string;
}

export interface PowerSyncQueryAllResult<T = Record<string, unknown>> {
  success: boolean;
  data: T[];
  error?: string;
}

export interface PowerSyncStatusData {
  connected: boolean;
  /** True while the PowerSync SDK is actively establishing a connection (before connected becomes true). */
  connecting: boolean;
  /** 'initial' | 'connecting' | 'connected' | 'downloading' | 'uploading' | 'disconnected' | 'error' */
  status: string;
  lastSyncedAt: string | null;
  hasSynced: boolean;
  uploadQueueSize: number;
  /** True while a sync checkpoint download is in flight. */
  downloading: boolean;
  /** Message from the most recent failed download attempt, or null if the last attempt (or the current one) succeeded. */
  downloadError: string | null;
  /** Message from the most recent failed upload attempt, or null if the last attempt (or the current one) succeeded. */
  uploadError: string | null;
}

export interface PowerSyncStatusResult {
  success: boolean;
  data: PowerSyncStatusData;
  error?: string;
}

export interface PowerSyncChangePayload {
  /** Names of the SQLite tables that changed (e.g. ['orders', 'order_items']). */
  tables: string[];
}

/**
 * Minimal shape both the real @powersync/web `SyncStatus` instance (web
 * build) and the Electron IPC facade (see electronDb.ts) satisfy. Decouples
 * useSyncStatus.ts / ipcBridge.ts from the @powersync/web package so they
 * work identically regardless of which PowerSync implementation is active.
 */
export interface RawPowerSyncStatus {
  connected: boolean;
  connecting?: boolean;
  hasSynced?: boolean;
  lastSyncedAt?: Date | null;
  dataFlowStatus?: {
    uploading?: boolean;
    downloading?: boolean;
    // The real @powersync/web SDK (web build) sets these to actual Error
    // objects; the Electron IPC path (serializeStatus in main/ipc/
    // powersyncHandlers.ts) already reduces them to plain strings before
    // crossing the IPC boundary (Error isn't structured-clone-able). Callers
    // should read these via extractSyncErrorMessage() below rather than
    // assuming either shape.
    downloadError?: Error | string | null;
    uploadError?: Error | string | null;
  };
}

/**
 * Normalizes either the web SDK's Error objects or the Electron IPC path's
 * already-stringified errors into a single string | null shape.
 *
 * Deliberately duck-types on `.message` rather than `instanceof Error` —
 * an error thrown by @powersync/web can fail an `instanceof Error` check
 * across a bundler/module-instance boundary while still having a real
 * `.message`. Relying on `instanceof` let a raw error object slip through
 * as-is in that case, which then crashed the UI when rendered directly
 * (React error #31: "Objects are not valid as a React child").
 */
export function extractSyncErrorMessage(err: unknown): string | null {
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: unknown }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }
  return 'An unknown sync error occurred';
}
